import type { HistoryData } from '~/vendor/cxy/types';

import styles from '../styles/BarChart.module.scss';
import { XAxisTick } from '../XAxisTick';

import { getGameDate, getOppositionTeamName } from './getXAxisData';

/**
 * Shared configuration for the two `XAxis` elements both charts render.
 *
 * PROP FACTORIES, NOT COMPONENTS, and that is not a style preference. recharts identifies its
 * children by component TYPE — it walks the element tree looking for `XAxis`, `YAxis`, `Bar` and so
 * on to build the chart — so wrapping these in a `<ChartAxes />` component hands it an unrecognised
 * type and the axes silently do not render. Spreading a props object leaves `<XAxis>` a direct child
 * of the chart, which is the shape recharts requires.
 *
 * `BarChart` and `BidirectionalBarChart` had both of these inline and identical, bar one `className`.
 */

/** The opposition-team axis: custom tick component, and a height that collapses on a wide container. */
export const primaryXAxisProps = ({
  americanFormat,
  isContainerWide,
}: {
  americanFormat: boolean;
  isContainerWide: boolean;
}) =>
  ({
    axisLine: false,
    className: styles.primaryAxis,
    dataKey: (historyData: HistoryData) => getOppositionTeamName(historyData, americanFormat),
    fontSize: 12,
    height: isContainerWide ? 5 : 45,
    interval: 0 as const,
    tick: <XAxisTick />,
    tickLine: false,
  }) as const;

/**
 * The game-date axis, on its own `xAxisId` so it stacks under the primary one.
 *
 * `className` is a parameter because only the bidirectional chart sets it on the element as well as
 * on the tick; the plain chart relies on the tick's class alone. Both are load-bearing for the specs
 * that read `.dateAxis tspan`.
 */
export const dateXAxisProps = ({ americanFormat, className }: { americanFormat: boolean; className?: string }) =>
  ({
    allowDuplicatedCategory: false,
    axisLine: false,
    ...(className === undefined ? {} : { className }),
    dataKey: (historyData: HistoryData) => getGameDate(historyData, americanFormat),
    dy: 5,
    fontSize: 10,
    tick: { fill: 'var(--cxy-color-content-default)', className: 'dateAxis' },
    tickLine: false,
    xAxisId: '1',
  }) as const;
